import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../store/slices/userSlice';
import { getOrCreateDirectMessage } from '../../store/slices/channelSlice';
import { sendMessage } from '../../store/slices/chatSlice';
import { addNotification } from '../../store/slices/uiSlice';
import PresignedAvatar from './PresignedAvatar';
import { extractEntityId } from '../../utils/directMessageUtils';
import {
  buildWorkItemShareUrl,
  normalizeWorkItemShare,
  workItemShareLabel,
} from '../../utils/workItemShare';
import { apiErrorMessage } from '../../utils/workflowTaskUtils';

const userLabel = (person) => {
  const name = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();
  return name || person?.username || 'User';
};

const WorkItemShareMenu = ({ share, onClose }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const { users = [] } = useSelector((state) => state.users);
  const [mode, setMode] = useState('choose');
  const [query, setQuery] = useState('');
  const [sharingUserId, setSharingUserId] = useState(null);
  const [copying, setCopying] = useState(false);

  const payload = normalizeWorkItemShare(share);

  useEffect(() => {
    dispatch(fetchUsers({ force: true, fromNetwork: true }));
  }, [dispatch]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || []).filter((person) => {
      const id = extractEntityId(person);
      if (!id || String(id) === String(currentUser?.id)) return false;
      if (!q) return true;
      const username = String(person.username || '').toLowerCase();
      return userLabel(person).toLowerCase().includes(q) || username.includes(q);
    });
  }, [users, query, currentUser?.id]);

  if (!payload) return null;

  const handleCopyLink = async () => {
    const url = buildWorkItemShareUrl(payload);
    if (!url) return;
    setCopying(true);
    try {
      await navigator.clipboard.writeText(url);
      dispatch(addNotification({ type: 'success', message: 'Link copied' }));
      onClose?.();
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Could not copy link' }));
    } finally {
      setCopying(false);
    }
  };

  const handleShareWithUser = async (person) => {
    const userId = extractEntityId(person);
    if (!userId || sharingUserId) return;
    setSharingUserId(userId);
    try {
      const channel = await dispatch(getOrCreateDirectMessage(userId)).unwrap();
      const channelId = extractEntityId(channel) || channel?.id;
      if (!channelId) throw new Error('Could not open conversation');
      await dispatch(sendMessage({
        content: '',
        channelId,
        senderId: currentUser?.id,
        workItemShare: payload,
      })).unwrap();
      dispatch(addNotification({
        type: 'success',
        message: `Shared with ${userLabel(person)}`,
      }));
      onClose?.();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to share'),
      }));
    } finally {
      setSharingUserId(null);
    }
  };

  const dialog = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close share"
        onClick={onClose}
      />
      <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Share</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
              {workItemShareLabel(payload)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {mode === 'choose' ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setMode('search')}
              className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60"
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Search user</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Send this as a card in a direct message
              </p>
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              disabled={copying}
              className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 disabled:opacity-50"
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {copying ? 'Copying…' : 'Copy link'}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Anyone with the link can open this popup
              </p>
            </button>
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setMode('choose')}
              className="text-[12px] font-semibold text-primary-600 dark:text-primary-400 mb-2"
            >
              Back
            </button>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people…"
              autoFocus
              className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white mb-2"
            />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-1 py-3">No people found</p>
              ) : (
                filteredUsers.slice(0, 40).map((person) => {
                  const id = extractEntityId(person);
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={Boolean(sharingUserId)}
                      onClick={() => handleShareWithUser(person)}
                      className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 disabled:opacity-50"
                    >
                      <PresignedAvatar
                        fileKey={person.profilePicture}
                        name={userLabel(person)}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                          {userLabel(person)}
                        </span>
                        {person.username ? (
                          <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            {person.username}
                          </span>
                        ) : null}
                      </span>
                      {sharingUserId === id ? (
                        <span className="ml-auto text-[11px] text-gray-400">Sending…</span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(dialog, document.body);
};

export default WorkItemShareMenu;
